import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { PhotoLightbox } from './PhotoLightbox';
import { useSignedUrls } from './useSignedUrls';
import { attachmentService } from '../../../services/attachmentService';
import { formatBytes, formatDuration } from '../../../utils/attachmentRules';

const heading = 'text-xs font-bold uppercase tracking-wider text-slate-400 mb-2';

export const AttachmentGallery = ({ attachments }) => {
  const urls = useSignedUrls(attachments.map((a) => a.storage_path));
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');
  if (!attachments.length) return null;

  const photos = attachments.filter((a) => a.kind === 'photo').map((a) => ({ ...a, url: urls[a.storage_path] }));
  const audios = attachments.filter((a) => a.kind === 'audio');
  const files = attachments.filter((a) => a.kind === 'file');

  const download = (file) => {
    setError('');
    attachmentService.download(file).catch(() => setError('Could not open that file. Please try again.'));
  };

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {photos.length > 0 && (
        <div>
          <p className={heading}>Photos</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => p.url && setOpen(i)}
                className="aspect-square overflow-hidden rounded-xl bg-slate-100"
                aria-label={`Open ${p.file_name}`}
              >
                {p.url ? (
                  <img src={p.url} alt={p.file_name} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform" />
                ) : (
                  <div className="h-full w-full animate-pulse bg-slate-100" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {audios.length > 0 && (
        <div>
          <p className={heading}>Recordings</p>
          <div className="space-y-2">
            {audios.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {a.file_name}{a.duration_seconds ? ` · ${formatDuration(a.duration_seconds)}` : ''}
                </p>
                {urls[a.storage_path] ? (
                  <audio controls preload="none" src={urls[a.storage_path]} className="mt-2 w-full" />
                ) : (
                  <p className="mt-2 text-xs text-slate-400">Loading…</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {files.length > 0 && (
        <div>
          <p className={heading}>Files</p>
          <div className="space-y-2">
            {files.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => download(f)}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 text-left hover:bg-slate-50"
              >
                <SafeIcon icon={FiIcons.FiFile} className="text-slate-400 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-800">{f.file_name}</span>
                  <span className="text-xs text-slate-400">
                    {(f.file_name.split('.').pop() || '').toUpperCase()} · {formatBytes(f.size_bytes)}
                  </span>
                </span>
                <SafeIcon icon={FiIcons.FiDownload} className="text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}
      {open !== null && <PhotoLightbox photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </div>
  );
};